/*
 * WHICH RIBBON IS UNDER THE POINTER (2026-10-19). Chris, looking at the crowded channel between two rows: "I am never able to hover on the ribbons that connects these 2 pages because there are so many
 * others there covering it up."
 *
 * Measured on the real page first (every ribbon's centre line sampled, and the browser asked what is topmost there): some ribbons could be hovered at NONE of the points of their own length (one 0 of 4,
 * another 2 of 9). The cause was the hover targets, not the pictures: each ribbon had an invisible target a few pixels wider than itself, the targets overlap wherever ribbons run side by side, and the
 * browser hands the pointer to whichever target is on top, wherever in that target the pointer is. A small ribbon's target (always at least 12 px) lay over a bigger ribbon's whole body and took it.
 * Zooming in cannot fix that: the overlap grows with the picture.
 *
 * So the browser is only asked WHETHER the pointer is over a flow, and this decides WHICH, from the geometry, by the rule a person uses by eye: the ribbon you can see under the pointer is the one in
 * front, and the smallest ribbons are drawn in front (flowRibbons.ts stacks them that way). Inside no ribbon's band but close to one or two: the nearest. Pure, so every case can be tested.
 */
export interface HitRibbon {
  key: string;
  weight: number;
  /** the ribbon's width in pixels */
  width: number;
  /** points along its centre line */
  centre: Array<{ x: number; y: number }>;
}

/** a ribbon this thin is still this wide to the pointer (half of it, each side of the centre line) */
export const MIN_HIT_HALF = 4;
/** how far outside its band a ribbon still catches the pointer */
export const HIT_MARGIN = 3;

/** The distance from a point to a polyline (the shortest, over every segment). Infinity for no points. */
export const distanceToPolyline = (px: number, py: number, line: Array<{ x: number; y: number }>): number => {
  if (!line.length) return Infinity;
  if (line.length === 1) return Math.hypot(px - line[0].x, py - line[0].y);
  let best = Infinity;
  for (let i = 0; i < line.length - 1; i++) {
    const a = line[i];
    const b = line[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / len2)) : 0;
    const d = Math.hypot(px - (a.x + dx * t), py - (a.y + dy * t));
    if (d < best) best = d;
  }

  return best;
};

/** `a` is drawn in front of `b`: the smaller ribbon, and for equal ones the later key (the order flowRibbons.ts stacks them in) */
export const inFront = (a: { weight: number; key: string }, b: { weight: number; key: string }): boolean => a.weight < b.weight || (a.weight === b.weight && a.key > b.key);

/**
 * The key of the ribbon the pointer is on, or null. Inside the band of one or more ribbons: the one in front. Otherwise within the margin of some: the one whose edge is nearest. Otherwise none.
 */
export const pickRibbonAt = (ribbons: HitRibbon[], x: number, y: number, margin: number = HIT_MARGIN): string | null => {
  let inside: HitRibbon | null = null;
  let near: HitRibbon | null = null;
  let nearOutside = Infinity;
  for (const r of ribbons) {
    const half = Math.max(MIN_HIT_HALF, r.width / 2);
    const d = distanceToPolyline(x, y, r.centre);
    if (d <= half) {
      if (!inside || inFront(r, inside)) inside = r;
    } else if (d <= half + margin && d - half < nearOutside) {
      near = r;
      nearOutside = d - half;
    }
  }

  return (inside ?? near)?.key ?? null;
};
